import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";

import {
  createEditorSession
} from "actions/editor";

import _ from "lodash";
import cx from "classnames";
import { toast } from "react-toastify";

import { SuccessToast, FailureToast } from "components/Toast";
import { Editor } from "components/Editor";
import { Loader } from "components/ApplicationLoader";
import { PageHeader, HeaderContext } from "../pages/components/header";

import { LIVE_ELEMENT_CONTAINER, PAGE } from "constants/widgetTypes";
import { fetchLiveElement, invalidateLiveElement, publishLiveElement, renameLiveElement, updateLiveElement } from "actions/live_elements";

import styles from "./liveElement.module.scss";
import { LiveElementHeader } from "./header";
import { Button } from "react-bootstrap";
import { getPageViewedEventPayload } from "services/mixpanelUtil";
import { MIXPANEL_PAGE_VIEWED, MIXPANEL_PAGE_VIEWED_EDITOR } from "constants/mixpanelEvents";
import { createMixpanelEvent } from "actions/mixpanel";

const LiveElementNameField = ({ liveElement, onSave }) => {
  const [edit, setEdit] = useState(false);
  const [name, setName] = useState("");

  const updating = useSelector((state) => _.get(state, `liveElements.liveElement.update.updating`));
  const updated = useSelector((state) => _.get(state, `liveElements.liveElement.update.updated`));

  useEffect(() => {
    if(edit && !updating && updated){
      setEdit(false);
    }
  }, [updating, updated]);

  useEffect(() => {
    if(liveElement){
      setName(liveElement.name);
    }
  }, [_.get(liveElement, 'name')]);

  const handleNameChange = (e) => {
    setName(e.target.value);
  }

  const handleSave = () => {
    if(onSave){
      onSave(name);
    }
  }

  return (
    <div className={styles.nameField}>
      <span className={styles.label}>Live Element:</span>
      <div className={styles.inputField}>
        {edit &&
          <>
            <input type="text" value={name} onChange={handleNameChange} />
            <div className={styles.actionGroup}>
              <button className={styles.action} disabled={updating} onClick={handleSave}>
                <i className="cleverstory-tick" />
              </button>
              <button className={styles.action} disabled={updating} onClick={() => setEdit(false)}>
                <i className="cleverstory-close" />
              </button>
            </div>
          </>
        }

        {!edit &&
          <>
            <div className={styles.name}>
              {_.get(liveElement, "name")}
            </div>
            <div className={styles.actionGroup}>
              <button className={styles.action} disabled={updating} onClick={() => setEdit(true)}>
                <i className="cleverstory-edit" />
              </button>
            </div>
          </>
        }
      </div>
    </div>
  );
}

export default function LiveElementEditorPage(props) {
  const dispatch = useDispatch();

  const { id } = useParams();

  const [headerContext, setHeaderContext] = useState({
    type: 'live_element',
    entity: {},
    editorSession: null,
    updateState: {},
    onSave: () => { },
    onRestore: () => { }
  });

  const liveElement = useSelector(state => _.get(state, `liveElements.liveElement.${id}.item`));
  const elementLoaded = _.get(liveElement, "id");
  const loadingElement = useSelector(state => _.get(state, `pages.page.${id}.loading`, false));

  const editorSession = React.useRef(`editors_${id}`);

  const updateState = useSelector((state) => _.get(state, `liveElements.liveElement.update`));
  const restoreState = useSelector((state) => _.get(state, `liveElements.liveElement.restore`));
  const publishState = useSelector((state) => _.get(state, `liveElements.liveElement.publish`));

  const updating = _.get(updateState, 'updating');
  const publishing = _.get(publishState, 'publishing');

  useEffect(() => {
    dispatch(fetchLiveElement(id));
    editorSession.current = `editors_${id}`;
  }, [id]);

  useEffect(() => {
    if (editorSession.current === `editors_${id}`) {
      initialiseEditor();
      setHeaderContext(headerContext => {
        return {
          ...headerContext,
          entity: liveElement,
          editorSession: editorSession.current,
          updateState: updateState,
          onSave: handlePageSave,
          onRestore: handlePageRestore
        }
      })
    }
  }, [editorSession.current, elementLoaded]);

  useEffect(() => {
    if (liveElement) {
      setHeaderContext(headerContext => {
        return {
          ...headerContext,
          entity: liveElement,
        }
      })
    }
  }, [liveElement]);

  useEffect(() => {
    var mixPanelPayload = getPageViewedEventPayload(MIXPANEL_PAGE_VIEWED_EDITOR)
    mixPanelPayload = {
      ...mixPanelPayload,
      editor_type: 'Live Element',
      device_type: 'Desktop'
    }
    dispatch(createMixpanelEvent(MIXPANEL_PAGE_VIEWED, mixPanelPayload))
  }, []);

  useEffect(() => {
    setHeaderContext(headerContext => {
      return {
        ...headerContext,
        updateState: updateState
      }
    })

    if (!updateState) {
      return;
    }

    if (updateState.updated) {
      toast(
        <SuccessToast
          heading={`Success`}
          description={`${renderToastName()} saved successfully`}
        />,
        {
          type: toast.TYPE.DEFAULT,
          position: toast.POSITION.BOTTOM_RIGHT,
          autoClose: 3000,
        }
      );

      dispatch(invalidateLiveElement(liveElement.id));
    } else if (updateState.error) {
      toast(
        <FailureToast
          heading={`Failure`}
          description={`Unable to save ${renderToastName()} live element`}
        />,
        {
          type: toast.TYPE.DEFAULT,
          position: toast.POSITION.BOTTOM_RIGHT,
          autoClose: 3000,
        }
      );

      dispatch(invalidateLiveElement(liveElement.id));
    }
  }, [updateState]);

  useEffect(() => {
    setHeaderContext(headerContext => {
      return {
        ...headerContext,
        publishState: publishState
      }
    })

    if (!publishState) {
      return;
    }

    if (publishState.published) {
      toast(
        <SuccessToast
          heading={`Success`}
          description={`${renderToastName()} published successfully`}
        />,
        {
          type: toast.TYPE.DEFAULT,
          position: toast.POSITION.BOTTOM_RIGHT,
          autoClose: 3000,
        }
      );

      dispatch(invalidateLiveElement(liveElement.id));
    } else if (updateState.error) {
      toast(
        <FailureToast
          heading={`Failure`}
          description={`Unable to publish ${renderToastName()} live element`}
        />,
        {
          type: toast.TYPE.DEFAULT,
          position: toast.POSITION.BOTTOM_RIGHT,
          autoClose: 3000,
        }
      );

      dispatch(invalidateLiveElement(liveElement.id));
    }
  }, [publishState]);

  const initialiseEditor = () => {
    const desktopGuides = _.get(liveElement, "draft.desktop.guides");
    const mobileGuides = _.get(liveElement, "draft.mobile.guides");
    const widgets = _.get(liveElement, "draft.widgets") || [];
    const fonts = _.get(liveElement, "draft.fonts") || [];

    const container = widgets.find(item => item.type === LIVE_ELEMENT_CONTAINER);

    const desktopHeight = parseInt(_.get(container, 'properties.desktop.layout.height'));
    const mobileHeight = parseInt(_.get(container, 'properties.desktop.layout.height'));

    _.set(container, 'properties.desktop.layout.width', '1200px');
    _.set(container, 'properties.desktop.layout.height', Math.max(600, desktopHeight));

    _.set(container, 'properties.mobile.layout.width', '375px');
    _.set(container, 'properties.mobile.layout.height', Math.max(600, mobileHeight));

    _.set(container, 'properties.desktop.container.minWidth', 1200);
    _.set(container, 'properties.mobile.container.minWidth', 375);

    const initialState = {
      desktop: {
        guides: desktopGuides
      },
      mobile: {
        guides: mobileGuides
      },
      widgets: widgets,
      entityType: 'LIVE_ELEMENT',
      fonts: fonts,
      themes: _.get(liveElement, 'themes'),
      interactive: {
        active: _.get(container, 'uid')
      }
    }

    dispatch(createEditorSession(editorSession.current, initialState));
  }

  const handlePageSave = () => {
    dispatch(updateLiveElement(liveElement.id, editorSession.current));
  };

  const handlePageRestore = () => {

  }

  const renderToastName = () => {
    var name = _.get(liveElement, 'name');
    return name;
  }

  const renderPageState = () => {
    return "Loading Editor..."
  }

  const canPublish = () => {
    if (publishing || updating) {
      return false;
    }

    return _.get(liveElement, 'changes');
  }

  const handleRename = (name) => {
    dispatch(renameLiveElement(liveElement.id, name));
  }

  const handleSave = () => {
    dispatch(updateLiveElement(liveElement.id, editorSession.current));
  }

  const handlePublish = () => {
    dispatch(publishLiveElement(liveElement.id));
  }

  return (
    <div className={cx(styles.liveElement, styles.editor, { [styles.loadingElement]: loadingElement || !elementLoaded })}>
      <Loader className={cx(styles.elementLoader, { [styles.loadingPage]: loadingElement || !elementLoaded })} title={renderPageState()} />
      <header className={styles.header}>
        <div className={cx(styles.menu, styles.primary)}>
          <div className={styles.logoContainer}>
            <div className={styles.logo}></div>
          </div>
          <LiveElementNameField liveElement={liveElement} onSave={handleRename} />
          <div className={cx(styles.actions, styles.right)}>
            <Button
              variant="light"
              className={cx(styles.saveButton, styles.actionButton)}
              disabled={updating}
              onClick={handleSave}
            >
              {updating ? 'Saving...' : 'Save Changes'}
            </Button>

            <Button
              disabled={!canPublish()}
              variant="primary"
              onClick={handlePublish}>
              {publishing ? 'Making Live...' : 'Make Live'}
            </Button>
          </div>
        </div>
        <HeaderContext.Provider value={headerContext}>
          <LiveElementHeader editorSession={editorSession.current} target={{
            id: id,
            type: 'live_element'
          }}></LiveElementHeader>
        </HeaderContext.Provider>
      </header>

      <Editor key={editorSession.current} id={editorSession.current} entityType='live_element' />
    </div>
  );
}